import type { Tenant, TenantTaskPriorityOption, TenantTaskStatusOption } from '~/types/models';

export const DEFAULT_TASK_STATUSES: TenantTaskStatusOption[] = [
    { value: 'Todo', label: 'Todo', color: '#64748b' },
    { value: 'In Progress', label: 'In Progress', color: '#2563eb' },
    { value: 'Blocked', label: 'Blocked', color: '#dc2626' },
    { value: 'Done', label: 'Done', color: '#16a34a' },
];

export const DEFAULT_TASK_PRIORITIES: TenantTaskPriorityOption[] = [
    { value: 'low', label: 'Low', color: '#2563eb' },
    { value: 'medium', label: 'Medium', color: '#d97706' },
    { value: 'high', label: 'High', color: '#ea580c' },
    { value: 'critical', label: 'Critical', color: '#dc2626' },
];

function normalizeColor(color: unknown, fallback: string) {
    const value = String(color || '').trim();
    return value || fallback;
}

function normalizeTaskOptions<T extends { value: string; label: string; color?: string }>(
    options: T[] | undefined,
    fallback: T[],
): T[] {
    if (!Array.isArray(options) || options.length === 0) {
        return fallback;
    }

    const seen = new Set<string>();

    return options
        .map((option, index) => {
            const value = String(option.value || option.label || '').trim();
            const label = String(option.label || option.value || '').trim();
            const fallbackOption = fallback[index] || fallback[0];

            if (!value || !label || seen.has(value.toLowerCase())) {
                return null;
            }

            seen.add(value.toLowerCase());

            return {
                value,
                label,
                color: normalizeColor(option.color, String(fallbackOption?.color || '#64748b')),
            } as T;
        })
        .filter((option): option is T => option !== null);
}

export function getTenantTaskStatuses(tenant?: Tenant | null): TenantTaskStatusOption[] {
    return normalizeTaskOptions(tenant?.taskSettings?.statuses, DEFAULT_TASK_STATUSES);
}

export function getTenantTaskPriorities(tenant?: Tenant | null): TenantTaskPriorityOption[] {
    return normalizeTaskOptions(tenant?.taskSettings?.priorities, DEFAULT_TASK_PRIORITIES);
}

export function getTaskStatusOption(value: string | undefined, options: TenantTaskStatusOption[]) {
    const normalized = String(value || '').trim().toLowerCase();
    return options.find(option => (
        option.value.trim().toLowerCase() === normalized
        || option.label.trim().toLowerCase() === normalized
    )) || null;
}

export function getTaskPriorityOption(value: string | undefined, options: TenantTaskPriorityOption[]) {
    const normalized = String(value || '').trim().toLowerCase();
    return options.find(option => (
        option.value.trim().toLowerCase() === normalized
        || option.label.trim().toLowerCase() === normalized
    )) || null;
}
